import { toAgentLanguage } from "@plannel/utils/i18n";

// 용어 사전 화면 공용 — 로케일·상태 판정·저장 페이로드. 로케일 코드는 AI 서버(semantics.LOCALES)와 같다.

export const LOCALES = ["ko", "en", "ja", "zh-TW", "zh-CN", "vi"];

// 언어 이름은 번역하지 않는다 — 앱 상단 언어 선택(SelectLanguage)과 같은 원어 표기
export const LOCALE_LABELS = {
  ko: "한국어", en: "English", ja: "日本語", "zh-TW": "中文(繁體)", "zh-CN": "中文(简体)", vi: "Tiếng Việt",
};

export const ALIAS_MAX_LEN = 32;
export const NAME_MAX_LEN = 100;
export const DEF_MAX_LEN = 500;
export const NOTE_MAX_LEN = 2000;
const BAD_ALIAS_RE = /[,?？]/;

export const STATUS = { STANDARD: "STANDARD", OVERRIDE: "OVERRIDE", ADDED: "ADDED" };

export const uiLocale = (lang) => toAgentLanguage(lang);

// 표시 순서만 — UI 언어를 맨 앞에, 나머지는 기본 순서. 저장 JSON 의 키 순서와는 무관
export const sortLocales = (first) => (LOCALES.includes(first) ? [first, ...LOCALES.filter((lg) => lg !== first)] : LOCALES);

export const rowStatus = (row) => {
  if (row.row_kind === "CUSTOM") return STATUS.ADDED;
  return row.override ? STATUS.OVERRIDE : STATUS.STANDARD;
};

// 표시 이름: 표시 언어 → en → ko → 나머지는 고정 순서(무작위면 다시 그릴 때마다 이름이 바뀐다). AI 서버 pick_name 과 같은 순서
const NAME_FALLBACK = ["en", "ko", ...LOCALES.filter((lg) => lg !== "en" && lg !== "ko")];
export const pickName = (names, locale) => {
  const n = names || {};
  const hit = [locale, ...NAME_FALLBACK].find((lg) => String(n[lg] || "").trim());
  return hit ? n[hit] : "";
};

export const termName = (row, locale) =>
  pickName(row.row_kind === "CUSTOM" ? row.override?.display_name : row.standard?.display_name, locale);

// 목록에 보일 정의 문장 — 테넌트 정의가 있으면 그것, 없으면 표준(glossary) 정의(개념에 표제어가 여럿이면 첫 줄)
export const definitionText = (row) => {
  const own = String(row.override?.def_txt || "").trim();
  if (own) return { text: own, custom: true };
  return { text: row.standard?.glossary_defs?.[0]?.definition || "", custom: false };
};

const lower = (s) => String(s || "").toLowerCase();

// 유효 별칭 = 표준 − 제외 + 추가 (AI 서버 tenant_terms.effective_aliases 와 같은 규칙)
export const effectiveAliases = (row, locale) => {
  const std = row.standard?.aliases?.[locale] || [];
  const gone = new Set((row.override?.excluded_aliases?.[locale] || []).map(lower));
  const out = std.filter((a) => !gone.has(lower(a)));
  const seen = new Set(out.map(lower));
  (row.override?.aliases?.[locale] || []).forEach((a) => {
    if (!seen.has(lower(a))) { out.push(a); seen.add(lower(a)); }
  });
  return out;
};

// 테넌트가 추가한 별칭(표준과 겹치면 표준으로 본다). 추가어 행은 표준이 없어 전부 해당
export const addedAliases = (row, locale) => {
  const std = new Set((row.standard?.aliases?.[locale] || []).map(lower));
  return new Set((row.override?.aliases?.[locale] || []).map(lower).filter((a) => !std.has(a)));
};

// 테넌트 값 표시 색의 종류(termColors.TERM_TONE 키) — 변경(표준 위 차분) / 추가(테넌트 고유 용어)
export const customTone = (row) => (row?.row_kind === "CUSTOM" || !row ? "added" : "override");

// 테넌트가 제외한 표준 별칭(표준에 아직 있는 것만) — 그리드에 취소선으로 보인다
export const excludedAliases = (row, locale) => {
  const std = new Set((row.standard?.aliases?.[locale] || []).map(lower));
  return (row.override?.excluded_aliases?.[locale] || []).filter((a) => std.has(lower(a)));
};

// 그리드 표시 순서 — 추가 → 표준 → 제외(추가를 표준 뒤에 두면 +N 에 가려진다). AI 쪽 순서와는 무관
export const displayAliases = (row, locale) => {
  const all = effectiveAliases(row, locale);
  const added = addedAliases(row, locale);
  return [...all.filter((a) => added.has(lower(a))), ...all.filter((a) => !added.has(lower(a))), ...excludedAliases(row, locale)];
};

export const isValidAlias = (a) => {
  const s = String(a || "").trim();
  return !!s && s.length <= ALIAS_MAX_LEN && !BAD_ALIAS_RE.test(s);
};

// 검색 대상 문자열 — 용어·개념ID·6개 언어의 호칭과 별칭
export const searchText = (row) => {
  const parts = [row.term_cd];
  const names = { ...(row.standard?.display_name || {}), ...(row.override?.display_name || {}) };
  Object.values(names).forEach((n) => parts.push(n));
  LOCALES.forEach((lg) => effectiveAliases(row, lg).forEach((a) => parts.push(a)));
  return parts.join("\n").toLowerCase();
};

// 편집 초안 — 모달 안에서만 쓰는 모양. 저장 시 toPayload 로 서버 형태로 바꾼다
export const toDraft = (row) => ({
  display_name: { ...(row?.override?.display_name || {}) },
  aliases: Object.fromEntries(LOCALES.map((lg) => [lg, [...(row?.override?.aliases?.[lg] || [])]])),
  excluded_aliases: Object.fromEntries(LOCALES.map((lg) => [lg, [...(row?.override?.excluded_aliases?.[lg] || [])]])),
  def_txt: row?.override?.def_txt || "",
  note_txt: row?.override?.note_txt || "",
});

const compact = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) =>
  (Array.isArray(v) ? v.length > 0 : !!String(v || "").trim())));

export const toPayload = (row, draft) => ({
  id: row?.id ?? null,
  term_cd: row?.term_cd || null,
  ver_num: row?.ver_num ?? 0,
  display_name: row && row.row_kind !== "CUSTOM" ? {} : compact(draft.display_name),
  aliases: compact(draft.aliases),
  excluded_aliases: compact(draft.excluded_aliases),
  def_txt: draft.def_txt.trim() || null,
  note_txt: draft.note_txt.trim() || null,
});

// 초안이 비어 있으면(표준 그대로) 저장 대신 되돌리기가 맞다
export const isEmptyDraft = (draft) =>
  !Object.values(draft.display_name).some((v) => String(v || "").trim())
  && !LOCALES.some((lg) => draft.aliases[lg].length || draft.excluded_aliases[lg].length)
  && !draft.def_txt.trim() && !draft.note_txt.trim();

// 서버 오류 detail({code, term, owner}) → 화면 문구
export const errorMessage = (t, e) => {
  const status = e?.response?.status;
  const d = e?.response?.data?.detail || {};
  if (status === 409) {
    if (d.code === "STALE") return t("aiTermConflictStale");
    if (d.code === "NOT_DEPLOYED") return t("aiTermTablesMissing");
    if (d.code === "DUPLICATE_STANDARD" || d.code === "DUPLICATE_TENANT") {
      return t("aiTermConflictDuplicate", { term: d.term || "", owner: d.owner || "" });
    }
  }
  if (status === 400) {
    if (d.code === "ALIAS_TOO_LONG" || d.code === "ALIAS_BAD_CHAR") return t("aiTermAliasInvalid");
    if (d.code === "DISPLAY_NAME_REQUIRED") return t("aiTermDisplayNameRequired");
    if (d.code === "NOTE_REQUIRES_DEF") return t("aiTermNoteRequiresDef");
    return t("aiTermInvalid");
  }
  return t("MSG_SaveFailed");
};
